import { AppIcons, College, addHttpIfMissing, collegeTransform } from '@backrs_ui/common-ui';

import { appStrings as commonAppStrings } from '@backrs_ui/common-ui/src/assets/appStrings';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { LubavButton } from '@backrs_ui/grow-ui/src/components/Lubav/LubavButton';
import { ResourceHeader } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceHeader';
import { ResourceWrapper } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/ResourceWrapper';
import { TheScoop } from '@backrs_ui/grow-ui/src/components/ResourceWrapper/TheScoop';
import {
  ResourcePageSection,
  ScoopStatItem,
} from '@backrs_ui/grow-ui/src/components/ResourceWrapper/types';
import { useGetCollegeProgramsQuery } from '@backrs_ui/grow-ui/src/redux/college/college.api';
import { useAppDispatch } from '@backrs_ui/grow-ui/src/redux/hooks';
import {
  openPlaylistListStep,
  setNuggetToAdd,
} from '@backrs_ui/grow-ui/src/redux/priorities/priorities.slice';

import { determineCollegeSize, determineDegreeType } from './college.util';
import { ReturnOnInvestment } from './molecules/ReturnOnInvestment';
import { TheCost } from './molecules/TheCost';
import { WhoGetsIn } from './molecules/WhoGetsIn';
import { visibleLevels } from './whatStudentsStudy/programStats';
import { WhatStudentsStudy } from './whatStudentsStudy/WhatStudentsStudy';

const COLLEGE_STRINGS = appStrings.modules.college;

type CollegePageProps = Readonly<{
  college: College;
  handleBookmark: () => void;
  hasBookmarked: boolean;
}>;

export const CollegePage = ({ college, handleBookmark, hasBookmarked }: CollegePageProps) => {
  const dispatch = useAppDispatch();
  const { data: programsData } = useGetCollegeProgramsQuery(String(college.id));
  const hasPrograms = programsData != null && visibleLevels(programsData.programs).length > 0;

  const summaryItems = [
    {
      title: COLLEGE_STRINGS.scoop.bestKnownFor,
      description: college.scoop_best_known_for || '',
    },
    {
      title: COLLEGE_STRINGS.scoop.vibeCheck,
      description: college.scoop_vibe_check || '',
    },
    {
      title: COLLEGE_STRINGS.scoop.opportunitiesNetworks,
      description: college.scoop_opportunities_networks || '',
    },
  ];

  const statItems: ScoopStatItem[] = [
    {
      kind: 'text',
      title: COLLEGE_STRINGS.scoop.enrollment,
      iconName: 'GraduationHat02',
      text: determineDegreeType(college.predominant_degree),
    },
    {
      kind: 'text',
      title: COLLEGE_STRINGS.scoop.type,
      iconName: 'Flag02',
      text: college.type || '',
    },
    {
      kind: 'text',
      title: COLLEGE_STRINGS.scoop.size,
      iconName: 'Building08',
      text: determineCollegeSize(college.undergrad_enrollment),
    },
    {
      kind: 'text',
      title: COLLEGE_STRINGS.scoop.setting,
      iconName: 'Building07',
      text: college.setting || '',
    },
  ];

  const whoGetsInButton = (
    <LubavButton
      id="college-who-gets-in-lubav-btn"
      label={COLLEGE_STRINGS.whoGetsIn.header.askLubav}
      prompt={`What are my chances of getting into ${college.school_name}? Look at the "Who Gets In" section on this page and compare the admissions data to my profile.`}
      allowTools={false}
      useGeneralKnowledge={false}
    />
  );

  const theCostButton = (
    <LubavButton
      id="college-the-cost-lubav-btn"
      label={COLLEGE_STRINGS.theCost.header.askLubav}
      prompt={`Help me lower the cost of attending ${college.school_name}. Look at the cost data on this page and find scholarships or financial aid opportunities.`}
      allowTools
      useGeneralKnowledge={false}
    />
  );

  const programsButton = (
    <LubavButton
      id="college-programs-lubav-btn"
      label={COLLEGE_STRINGS.whatStudentsStudy.header.askLubav}
      prompt={COLLEGE_STRINGS.whatStudentsStudy.header.lubavPrompt(college.school_name)}
      allowTools={false}
      useGeneralKnowledge={false}
    />
  );

  const roiButton = (
    <LubavButton
      id="college-roi-lubav-btn"
      label={COLLEGE_STRINGS.returnOnInvestment.header.askLubav}
      prompt={`Explain the return on investment data for ${college.school_name}. Look at the "Return on Investment" section on this page, including the success rate, median earnings, graduation rate, and the ROI horizon chart showing returns over 10 to 40 years.`}
      allowTools={false}
      useGeneralKnowledge={false}
    />
  );

  const PAGE_SECTIONS: ResourcePageSection[] = [
    {
      id: 'the-scoop',
      title: 'The Scoop',
      hideHeader: true,
      component: <TheScoop summaryItems={summaryItems} statItems={statItems} />,
    },
    {
      id: 'who-gets-in',
      title: COLLEGE_STRINGS.whoGetsIn.header.title,
      description: COLLEGE_STRINGS.whoGetsIn.header.description(college.school_name),
      headerRightComponents: [whoGetsInButton],
      component: <WhoGetsIn college={college} />,
    },
    {
      id: 'the-cost',
      title: COLLEGE_STRINGS.theCost.header.title,
      description: COLLEGE_STRINGS.theCost.header.description,
      headerRightComponents: [theCostButton],
      component: <TheCost college={college} />,
    },
    {
      id: 'roi',
      title: COLLEGE_STRINGS.returnOnInvestment.header.title,
      description: COLLEGE_STRINGS.returnOnInvestment.header.description,
      headerRightComponents: [roiButton],
      component: <ReturnOnInvestment college={college} />,
    },
    {
      id: 'what-students-study',
      title: COLLEGE_STRINGS.whatStudentsStudy.header.title,
      description: COLLEGE_STRINGS.whatStudentsStudy.header.description,
      headerRightComponents: [programsButton],
      visible: hasPrograms,
      component: programsData ? (
        <WhatStudentsStudy schoolName={college.school_name} data={programsData} />
      ) : null,
    },
  ];

  return (
    <ResourceWrapper
      header={
        <ResourceHeader
          title={college.school_name}
          banner_media_url={college.banner_media_url || undefined}
          nugget={collegeTransform(college)}
          logo_media_url={college.logo_media_url || '/images/university-logo-default.svg'}
          city={college.city}
          state={college.state}
          buttonMain={{
            label: COLLEGE_STRINGS.header.buttons.website,
            onClick: () => {
              if (college.url) {
                window.open(addHttpIfMissing(college.url), '_blank');
              }
            },
            icon: 'Link01',
          }}
          buttonGroup={[
            {
              onClick: handleBookmark,
              icon: hasBookmarked ? 'BookmarkCheck' : 'Bookmark',
              tooltipHoverText: commonAppStrings.modules.actionTabs.bookmark,
            },
            {
              onClick: async () => {
                await navigator.clipboard.writeText(college.url);
              },
              icon: 'Share06',
              tooltipOnClickText: COLLEGE_STRINGS.header.buttons.share,
              tooltipHoverText: commonAppStrings.modules.actionTabs.share,
            },
            {
              onClick: () => {
                dispatch(setNuggetToAdd(collegeTransform(college)));
                dispatch(openPlaylistListStep());
              },
              icon: 'BookmarkAdd' as AppIcons,
              tooltipHoverText: commonAppStrings.modules.actionTabs.task,
            },
          ]}
        />
      }
      sections={PAGE_SECTIONS}
      showTabs={false}
      chipLabel={college.school_name}
    />
  );
};
